import { type ReactNode } from 'react'
import type { ComponentType } from 'react'
import { NavLink, useLocation, type NavLinkProps, useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars, from, until } from '@ovasabi/ui-minimal/tokens'
import { useEdition } from '../../edition/EditionProvider'
import type { DockIconKey, DockTab } from '../../lib/edition'
import {
  BasketIcon,
  BoltIcon,
  CardIcon,
  ChatIcon,
  ChefHatIcon,
  HomeIcon,
  type IconProps,
  LifebuoyIcon,
  PlateIcon,
  RouteIcon,
  ShieldIcon,
  StorefrontIcon,
  UserIcon,
  WalletIcon,
} from '../icons'
import { desktopGutter, paddedMaxWidth } from './layout'

/*
 * Linaria's styled() needs a string className; NavLink also accepts a function of
 * its active state. Only the string form is used here.
 */
const NavLinkBase = NavLink as ComponentType<Omit<NavLinkProps, 'className'> & { className?: string }>


const GLYPH: Record<DockIconKey, (props: IconProps) => ReactNode> = {
  home: HomeIcon,
  chef: ChefHatIcon,
  dish: PlateIcon,
  market: StorefrontIcon,
  wallet: CardIcon,
  inbox: ChatIcon,
  help: LifebuoyIcon,
  cart: BasketIcon,
  profile: UserIcon,
  offers: BoltIcon,
  active: RouteIcon,
  earnings: WalletIcon,
  safety: ShieldIcon,
}

const Style = {
  Nav: styled.header`
    display: none;
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 100;
    width: 100%;
    background: ${minimalVars.color.bgElevated};
    /* bgElevated is opaque, so dropping the blur in a cheaper tier changes nothing visible. */
    backdrop-filter: var(--chow-backdrop-filter, blur(16px));
    -webkit-backdrop-filter: var(--chow-backdrop-filter, blur(16px));
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    box-shadow: none;
    box-sizing: border-box;

    &[data-mobile-actions='true'] { display: block; }
    ${from('page')} {
      display: block;
    }
  `,
  /* Same content column as the page header and body (layout.ts). */
  Container: styled.div`
    max-width: ${paddedMaxWidth};
    margin: 0 auto;
    padding: 0 ${desktopGutter}px;
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-width: 0;
    flex-wrap: wrap;
    box-sizing: border-box;
    ${until('page')} {
      min-height: 60px;
      padding: calc(8px + env(safe-area-inset-top, 0px)) 14px 8px;
      gap: 12px;
      flex-wrap: nowrap;
    }
  `,
  BrandGroup: styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    min-height: 44px;
    cursor: pointer;
    text-decoration: none;
    transition: opacity 140ms ease;
    /* Button reset: this is a control for behaviour's sake, not for looks. */
    appearance: none;
    background: none;
    border: 0;
    margin: 0;
    padding: 0;
    font: inherit;
    color: inherit;
    text-align: left;

    &:hover {
      opacity: 0.85;
    }

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.brand};
      outline-offset: 4px;
      border-radius: ${minimalVars.radius.sm};
    }
  `,
  BrandLogoTile: styled.span`
    width: 32px;
    height: 32px;
    border-radius: ${minimalVars.radius.sm};
    background: ${minimalVars.color.bgSurface};
    border: 1px solid ${minimalVars.color.borderSubtle};
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    ${until('page')} { display: none; }
  `,
  BrandTitle: styled.span`
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: 800;
    /* A wordmark is not a heading: h2Size is the 22px section-header size, so the logo lockup stays fixed. */
    font-size: 1.3rem;
    letter-spacing: -0.03em;
    color: ${minimalVars.color.textPrimary};
    min-width: 0;
    ${until('page')} {
      font-size: 1.1rem;
      img { display: block; width: 132px; max-width: 100%; height: auto; }
    }
  `,
  BrandTail: styled.span`
    color: ${minimalVars.color.brand};
  `,
  LinksGroup: styled.nav`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
    ${until('page')} { display: none; }
  `,
  Tab: styled(NavLinkBase)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 12px;
    text-decoration: none;
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textSecondary};
    border: 1px solid transparent;
    transition: color 160ms ease, background-color 160ms ease, border-color 160ms ease;
    border-radius: 10px;

    &.active {
      color: ${minimalVars.color.brand};
      background: ${minimalVars.color.brandSoft};
      border-color: ${minimalVars.color.borderSubtle};
    }

    &:hover:not(.active) {
      color: ${minimalVars.color.textPrimary};
      background: ${minimalVars.color.bgSurfaceAlt};
    }

    & > svg {
      flex-shrink: 0;
    }
  `,
  Badge: styled.span`
    /* A 10px count beside a 13px tab label: metaSize would match the label. */
    font-size: 10px;
    font-weight: ${minimalVars.typography.weightBold};
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    padding: 1px 5px;
    border-radius: 2px;
    line-height: 1.2;
  `,
  ActionsGroup: styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    ${until('page')} { display: none; }
  `,
  MobileActions: styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 8px;
    ${from('page')} { display: none; }
  `,
  MsgBtn: styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    background: ${minimalVars.color.bgSurface};
    color: ${minimalVars.color.textSecondary};
    border: 1px solid ${minimalVars.color.borderSubtle};
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    cursor: pointer;
    border-radius: 14px;
    transition: color 160ms ease, border-color 160ms ease, background-color 160ms ease;

    &:hover {
      color: ${minimalVars.color.brand};
      border-color: ${minimalVars.color.brand};
      background: ${minimalVars.color.brandSoft};
    }
  `,
  MsgBadge: styled.span`
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    font-weight: ${minimalVars.typography.weightBold};
    /* A 10px count badge stays fixed for the same reason as Style.Badge. */
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 999px;
  `,
  CartBtn: styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    border: 1px solid ${minimalVars.color.brand};
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    cursor: pointer;
    border-radius: 14px;
    transition: filter 160ms ease, transform 120ms ease;

    &:hover {
      filter: brightness(1.08);
      transform: translateY(-1px);
    }
  `,
  CartCount: styled.span`
    background: ${minimalVars.color.textInverse};
    color: ${minimalVars.color.brand};
    font-weight: ${minimalVars.typography.weightBold};
    /* An 11px count inside a brand pill stays fixed: metaSize matches the label. */
    font-size: 11px;
    padding: 1px 6px;
    border-radius: 2px;
  `,
}

export interface AppTopNavProps {
  badges?: Record<string, number>
  tabs?: readonly DockTab[]
  cartCount?: number
  mobileActions?: ReactNode
  showCart?: boolean
}

export const AppTopNav = ({ badges, tabs: overrideTabs, cartCount = 0, mobileActions, showCart = true }: AppTopNavProps) => {
  const { profile, edition: editionKey } = useEdition()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const tabs = overrideTabs ?? profile?.dockTabs ?? []
  const isChooseChow = editionKey !== 'chowdash'
  // What the wordmark actually spells, so the control's accessible name is the
  // brand a sighted user reads rather than a second, differently-worded one.
  const brandName = isChooseChow ? 'ChooseChow' : (profile?.label ?? 'ChowDash')

  return (
    <Style.Nav data-mobile-actions={Boolean(mobileActions)}>
      <Style.Container>
        <Style.BrandGroup
          type="button"
          aria-label={`${brandName}, go to home`}
          onClick={() => navigate(isChooseChow ? '/dashboard' : '/')}
        >
          <Style.BrandLogoTile>
            <img src={isChooseChow ? "/brand/choosechow-mascot.webp" : "/brand/chowdash-mark.webp"} width="32" height="32" alt="" />
          </Style.BrandLogoTile>
          <Style.BrandTitle>
            {isChooseChow ? (
              <img src="/brand/choosechow-wordmark.webp" width="156" height="23" alt="ChooseChow" />
            ) : (
              profile?.label ?? 'ChowDash'
            )}
          </Style.BrandTitle>
        </Style.BrandGroup>

        <Style.LinksGroup>
          {tabs.map((tab) => {
            const Glyph = GLYPH[tab.icon]
            const count = badges?.[tab.path]
            return (
              <Style.Tab key={tab.path} to={tab.path} className={tab.path === '/dishes' && ['/menus', '/kitchens'].includes(pathname) ? 'active' : undefined} end={tab.path === '/' || tab.path === '/dashboard'}>
                {Glyph ? <Glyph size="sm" /> : null}
                <span>{tab.label}</span>
                {count ? <Style.Badge>{count}</Style.Badge> : null}
              </Style.Tab>
            )
          })}
        </Style.LinksGroup>

        <Style.ActionsGroup>
          <Style.MsgBtn
            onClick={() => navigate('/inbox')}
            aria-label={`Messages ${badges?.['/inbox'] ? `(${badges['/inbox']} unread)` : ''}`}
          >
            <ChatIcon size="sm" />
            <span>Messages</span>
            {badges?.['/inbox'] ? <Style.MsgBadge>{badges['/inbox']}</Style.MsgBadge> : null}
          </Style.MsgBtn>
          {isChooseChow && showCart ? (
            <Style.CartBtn onClick={() => navigate('/cart')} aria-label={`Cart with ${cartCount} item${cartCount === 1 ? '' : 's'}`}>
              <BasketIcon size="sm" />
              <span>Cart</span>
              <Style.CartCount>{cartCount}</Style.CartCount>
            </Style.CartBtn>
          ) : null}
        </Style.ActionsGroup>
        {mobileActions && <Style.MobileActions aria-label="Quick actions">{mobileActions}</Style.MobileActions>}
      </Style.Container>
    </Style.Nav>
  )
}
